import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { createOpticalGrade } from './postfx.js';
import { FULLSCREEN_VERTEX_SHADER, VESPER_FRAGMENT_SHADER } from './vesper-field.js';

function seedFloat(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

export function detectQuality(params = new URLSearchParams(location.search)) {
  const requested = Number(params.get('q'));
  const mobile = matchMedia('(max-width: 700px)').matches;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const capableDesktop = !mobile
    && innerWidth >= 1100
    && (navigator.hardwareConcurrency || 4) >= 8;
  const tier = Number.isFinite(requested)
    ? THREE.MathUtils.clamp(Math.round(requested), 0, 2)
    : (mobile ? 0 : capableDesktop ? 2 : 1);

  return {
    tier,
    mobile,
    reducedMotion,
    steps: tier === 2 ? 42 : tier === 1 ? 30 : 19,
    pixelRatio: tier === 2 ? 1.18 : tier === 1 ? 0.86 : 0.58,
    bloom: tier > 0,
  };
}

export function createEngine(canvas, {
  seed = 'vesper-09',
  quality = detectQuality(),
  frozen = false,
} = {}) {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: quality.tier > 0,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(devicePixelRatio, quality.pixelRatio));
  renderer.setSize(innerWidth, innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const uniforms = {
    uTime: { value: 0 },
    uProgress: { value: 0 },
    uAspect: { value: innerWidth / innerHeight },
    uSeed: { value: seedFloat(seed) * 97.0 },
    uQuality: { value: quality.steps },
    uLook: { value: new THREE.Vector2() },
    uMotion: { value: quality.reducedMotion ? 0 : 1 },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: FULLSCREEN_VERTEX_SHADER,
    fragmentShader: VESPER_FRAGMENT_SHADER,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const geometry = new THREE.PlaneGeometry(2, 2);
  const field = new THREE.Mesh(geometry, material);
  field.frustumCulled = false;
  scene.add(field);

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  // Live desktop retains the real mip-chain bloom path. Frozen QA frames use
  // the deterministic optical-pass kernel so identical state stays hashable.
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(innerWidth, innerHeight),
    quality.bloom ? 0.72 : 0.22,
    0.58,
    0.64,
  );
  bloom.enabled = quality.bloom && !frozen;
  composer.addPass(bloom);

  const grade = new ShaderPass(createOpticalGrade());
  grade.uniforms.uDeterministicBloom.value = frozen ? (quality.bloom ? 1 : 0.68) : 0;
  composer.addPass(grade);

  let raf = 0;
  let last = performance.now();
  let callback = null;

  function resize() {
    renderer.setPixelRatio(Math.min(devicePixelRatio, quality.pixelRatio));
    renderer.setSize(innerWidth, innerHeight, false);
    composer.setSize(innerWidth, innerHeight);
    uniforms.uAspect.value = innerWidth / innerHeight;
    grade.uniforms.uAspect.value = innerWidth / innerHeight;
    const bufferSize = renderer.getDrawingBufferSize(new THREE.Vector2());
    grade.uniforms.uTexel.value.set(1 / bufferSize.x, 1 / bufferSize.y);
  }

  resize();

  function frame(now) {
    const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
    last = now;
    const elapsed = now / 1000;
    if (callback) callback(dt, elapsed);
    const visualTime = frozen
      ? uniforms.uProgress.value * 120.0 + uniforms.uSeed.value
      : elapsed;
    uniforms.uTime.value = visualTime;
    grade.uniforms.uTime.value = visualTime;
    composer.render();
    raf = requestAnimationFrame(frame);
  }

  function update({ progress, lookX = 0, lookY = 0, warp = 0 }) {
    uniforms.uProgress.value = progress;
    uniforms.uLook.value.set(lookX, lookY);
    grade.uniforms.uWarp.value = warp;
    grade.uniforms.uProgress.value = progress;
    bloom.strength = quality.bloom ? 0.70 + warp * 0.24 : 0.22;
    bloom.threshold = warp > 0.12 ? 0.72 : 0.64;
  }

  function start(fn) {
    callback = fn;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }

  addEventListener('resize', resize);

  return {
    renderer,
    composer,
    uniforms,
    bloom,
    grade,
    quality,
    update,
    start,
    dispose() {
      cancelAnimationFrame(raf);
      removeEventListener('resize', resize);
      geometry.dispose();
      material.dispose();
      composer.dispose();
      renderer.dispose();
    },
  };
}
